/**
 * Flag artwork for the client (bundler-only: SVG files are imported raw). The server imports
 * the package root, which has no artwork.
 */
import { getNation, type NationId } from './index.ts';
import { starsAndStripes } from './starsAndStripes.ts';

const files = import.meta.glob<string>('../flags/*.svg', { eager: true, query: '?raw', import: 'default' });

export interface FlagArt {
  /** Flag size in its own units; the hoist is at x = 0. */
  readonly width: number;
  readonly height: number;
  /** Inner SVG markup (no wrapping <svg>), to place inside an <svg> or <symbol>. */
  readonly body: string;
}

function parse(svg: string): FlagArt {
  const viewBox = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
  if (!viewBox) throw new Error('Flag SVG needs viewBox="0 0 w h"');
  const body = svg
    .replace(/^[\s\S]*?<svg[^>]*>/, '')
    .replace(/<\/svg>\s*$/, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/>\s+</g, '><')
    .trim();
  return { width: Number(viewBox[1]), height: Number(viewBox[2]), body };
}

const cache = new Map<NationId, FlagArt>();

export function flagArt(id: NationId): FlagArt {
  const hit = cache.get(id);
  if (hit) return hit;
  const nation = getNation(id);
  const svg = nation.flag ? starsAndStripes(nation.flag.stars) : files[`../flags/${id}.svg`];
  if (!svg) throw new Error(`Missing flags/${id}.svg`);
  const art = parse(svg);
  cache.set(id, art);
  return art;
}

/** A standalone SVG document of the flag, e.g. for <img src> or a favicon. */
export function flagSvg(id: NationId): string {
  const { width, height, body } = flagArt(id);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${body}</svg>`;
}
